import { CompositionElement } from '../models/elements/CompositionElement';
import { EditorChangeType } from '../types';
import { ElementTypeTransitionCommand } from './ElementTypeTransitionCommand';

import type { Element } from '../models/Element';
import type { VideoEditor } from '../VideoEditor';
import type { Command } from './Command';

export class UpdateElementCommand implements Command {
  private editor: VideoEditor;
  private readonly _shouldCreateUndoRedoEntry: boolean;

  getName(): string {
    return 'UpdateElementCommand';
  }

  shouldCreateUndoRedoEntry(): boolean {
    return this._shouldCreateUndoRedoEntry;
  }

  private elementId: string;
  private newProps: Record<string, any>;
  private oldProps: Record<string, any> = {};
  private element?: Element;
  private executed: boolean = false;

  constructor(
    editor: VideoEditor,
    elementId: string,
    props: Record<string, any>,
    createUndoEntry: boolean = true,
  ) {
    this.editor = editor;
    this.elementId = elementId;
    this.element = this.editor.getElementById(elementId);
    this._shouldCreateUndoRedoEntry = createUndoEntry;

    // Apply frame snapping to time and duration
    this.newProps = { ...props };
    if (typeof this.newProps.time === 'number') {
      this.newProps.time = this.editor.timeline.snapTimeToFrame(
        this.newProps.time,
      );
    }
    if (typeof this.newProps.duration === 'number') {
      this.newProps.duration = this.editor.timeline.snapTimeToFrame(
        this.newProps.duration,
      );
    }

    // Store original values for undo
    if (this.element) {
      for (const key in this.newProps) {
        this.oldProps[key] = this.element[key as keyof Element];
      }
    }
  }

  /**
   * Factory method that returns either UpdateElementCommand or ElementTypeTransitionCommand
   * based on whether a type transition is needed
   */
  static create(
    editor: VideoEditor,
    elementId: string,
    props: Record<string, any>,
    createUndoEntry: boolean = true,
  ): Command {
    const element = editor.getElementById(elementId);
    if (!element) {
      return new UpdateElementCommand(
        editor,
        elementId,
        props,
        createUndoEntry,
      );
    }

    // Check if this update requires a type transition
    const transitionInfo = UpdateElementCommand.detectTypeTransition(
      element,
      props,
    );
    if (transitionInfo.needsTransition) {
      // As of now, this type of update should always create an undo entry,
      // so no need to pass createUndoEntry since it defaults to true
      return new ElementTypeTransitionCommand(
        editor,
        elementId,
        transitionInfo.targetType!,
        props,
      );
    }

    // No transition needed, use regular update
    return new UpdateElementCommand(editor, elementId, props, createUndoEntry);
  }

  /**
   * Detects if the property update requires a type transition
   */
  public static detectTypeTransition(
    element: Element,
    props: Record<string, any>,
  ): { needsTransition: boolean; targetType?: 'image' | 'composition' } {
    // Check if fit property is being changed
    if (!props.fit) {
      return { needsTransition: false };
    }

    const currentIsComposition = element instanceof CompositionElement;
    const fitValue = props.fit;

    // Case 1: Changing to black-frame fit (needs CompositionElement)
    if (fitValue === 'black-frame') {
      if (!currentIsComposition) {
        return { needsTransition: true, targetType: 'composition' };
      }
    }
    // Case 2: Changing from black-frame to regular fit (needs ImageElement)
    else if (
      fitValue === 'cover' ||
      fitValue === 'contain' ||
      fitValue === 'fill'
    ) {
      if (currentIsComposition) {
        return { needsTransition: true, targetType: 'image' };
      }
    }

    return { needsTransition: false };
  }

  execute(): boolean {
    if (!this.element) return false;

    const success = this.editor.timeline.updateElement(
      this.elementId,
      this.newProps,
    );
    if (success) {
      this.executed = true;
      this.editor.markDirty('update_element_command');
      this.editor.notifyObservers(EditorChangeType.ELEMENT_UPDATED, {
        elementId: this.elementId,
        element: this.element,
        properties: this.newProps,
      });
    }
    return success;
  }

  undo(): boolean {
    if (!this.executed || !this.element) return false;

    const success = this.editor.timeline.updateElement(
      this.elementId,
      this.oldProps,
    );
    if (success) {
      this.executed = false;
      this.editor.markDirty('update_element_command_undo');
      this.editor.notifyObservers(EditorChangeType.ELEMENT_UPDATED, {
        elementId: this.elementId,
        element: this.element,
        properties: this.oldProps,
      });
    }
    return success;
  }

  redo(): boolean {
    if (this.executed) return false;
    return this.execute();
  }

  getDescription(): string {
    return `Update element ${this.elementId} properties: ${Object.keys(
      this.newProps,
    ).join(', ')}`;
  }
}
